/**
 * Standalone chunk carrying activity glyphs and the config-only registry tail
 * (`dist/icons-extra.js`), fetched the first time `renderLucideIcon` is asked
 * for one of these names (see EXTRA_ICON_NAMES in utils/icons.ts). Keys MUST
 * stay in sync with that list (asserted by `icons-partition.test.ts`).
 *
 * Loaded via `icons-extra-loader.ts`; both IIFE entries (`index-global.ts`
 * and `launcher-global.ts`) register sibling-URL loaders for it.
 */
import type { IconNode } from "lucide";
import {
  Brain,
  Toolbox,
  Wrench,
  Ban,
  Bell,
  Bookmark,
  Building,
  Calendar,
  CalendarDays,
  Camera,
  ChartColumn,
  Circle,
  CircleCheck,
  CircleX,
  Clock,
  CreditCard,
  DollarSign,
  Database,
  Terminal,
  EllipsisVertical,
  EyeOff,
  Files,
  Film,
  Flag,
  Folder,
  FolderOpen,
  Funnel,
  Gift,
  Globe,
  Hash,
  Headphones,
  Heart,
  House,
  Image as ImageIcon,
  Info,
  Key,
  Lightbulb,
  Link,
  Lock,
  Mail,
  MapPin,
  MessageCircle,
  Monitor,
  Moon,
  Package,
  Pause,
  PenLine,
  Percent,
  Phone,
  Play,
  Receipt,
  RotateCw,
  Save,
  Settings,
  Share,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Store,
  Sun,
  Tag,
  Timer,
  TriangleAlert,
  Truck,
  User,
  VolumeX,
  Wallet,
  Zap,
} from "lucide";

export const EXTRA_LUCIDE_ICONS = {
  "ellipsis-vertical": EllipsisVertical,
  "brain": Brain,
  "toolbox": Toolbox,
  "wrench": Wrench,
  "circle": Circle,
  "database": Database,
  "terminal": Terminal,
  "user": User,
  "mail": Mail,
  "phone": Phone,
  "calendar": Calendar,
  "clock": Clock,
  "building": Building,
  "map-pin": MapPin,
  "lock": Lock,
  "key": Key,
  "credit-card": CreditCard,
  "hash": Hash,
  "globe": Globe,
  "link": Link,
  "circle-check": CircleCheck,
  "circle-x": CircleX,
  "triangle-alert": TriangleAlert,
  "info": Info,
  "ban": Ban,
  "shield": Shield,
  "house": House,
  "pen-line": PenLine,
  "save": Save,
  "share": Share,
  "funnel": Funnel,
  "settings": Settings,
  "rotate-cw": RotateCw,
  "shopping-cart": ShoppingCart,
  "shopping-bag": ShoppingBag,
  "package": Package,
  "truck": Truck,
  "tag": Tag,
  "gift": Gift,
  "receipt": Receipt,
  "wallet": Wallet,
  "store": Store,
  "dollar-sign": DollarSign,
  "percent": Percent,
  "play": Play,
  "pause": Pause,
  "volume-x": VolumeX,
  "camera": Camera,
  "image": ImageIcon,
  "film": Film,
  "headphones": Headphones,
  "message-circle": MessageCircle,
  "bell": Bell,
  "heart": Heart,
  "eye-off": EyeOff,
  "bookmark": Bookmark,
  "calendar-days": CalendarDays,
  "timer": Timer,
  "folder": Folder,
  "folder-open": FolderOpen,
  "files": Files,
  "chart-column": ChartColumn,
  "sparkles": Sparkles,
  "zap": Zap,
  "sun": Sun,
  "moon": Moon,
  "flag": Flag,
  "lightbulb": Lightbulb,
  "monitor": Monitor,
  "smartphone": Smartphone,
} as const satisfies Record<string, IconNode>;
